import { useCallback, useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Dialog, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import type { DraftZone } from '../model/builder.store.js';
import {
  placeRect,
  rectWithinFloor,
  rectsOverlap,
  type WarehouseFloor,
} from '../model/plan-geometry.js';
import { ZoneEditor, type ZoneFormValues } from './ZoneEditor.js';

/**
 * ZONA OYNASI — qo'shish ham, tahrirlash ham.
 *
 * Ilgari zona rejaga sudrab chizilardi va bu ikki narsani birdan
 * so'rardi: "qanday zona" va "qayerda turadi". Natijada odam sichqoncha
 * bilan taxminiy to'rtburchak chizib, keyin uning o'lchamini xossalar
 * panelida qo'lda tuzatardi — ya'ni ish ikki marta qilinardi.
 *
 * Endi savol bitta: qanday zona va qanchalik katta. Joyni `placeRect`
 * ombor ichidan o'zi topadi va mavjud zonalar bilan kesishmaydi — bir
 * kvadrat metr ikki zonaga tegishli bo'lishi mumkin emas.
 */
export interface ZoneDialogProps {
  open: boolean;
  onClose: () => void;
  zones: DraftZone[];
  floor: WarehouseFloor;
  /** `null` — yangi zona; aks holda shu zona tahrirlanadi. */
  zone?: DraftZone | null;
  onSubmit: (values: ZoneFormValues) => void;
}

/** Navbatdagi zona kodi: A, B, C … Bandlari o'tkazib yuboriladi. */
const nextCode = (zones: readonly DraftZone[]): string => {
  const used = new Set(zones.map((zone) => zone.code));
  for (let index = 0; index < 26; index += 1) {
    const code = String.fromCharCode(65 + index);
    if (!used.has(code)) return code;
  }
  return `Z${zones.length}`;
};

export const ZoneDialog = ({
  open,
  onClose,
  zones,
  floor,
  zone = null,
  onSubmit,
}: ZoneDialogProps) => {
  const [draft, setDraft] = useState<ZoneFormValues | null>(null);

  const taken = useMemo(
    () => zones.filter((other) => other.id !== zone?.id).map((other) => other.plan),
    [zones, zone],
  );

  /*
   * TAHRIRLASHDA zona O'Z JOYIDA qoladi — agar yangi o'lcham u yerga hali
   * ham sig'sa. Aks holda odam nomni tuzatgani uchun butun zona rejaning
   * boshqa burchagiga sakrab ketardi.
   */
  const spot = useMemo(() => {
    if (draft === null) return null;
    const size = { w: draft.w, d: draft.d };
    if (zone !== null) {
      const stays = { x: zone.plan.x, y: zone.plan.y, ...size };
      const clash = taken.some((other) => rectsOverlap(other, stays));
      if (!clash && rectWithinFloor(stays, floor)) return { x: stays.x, y: stays.y };
    }
    return placeRect(size, taken, floor);
  }, [draft, taken, floor, zone]);

  const handleChange = useCallback((values: ZoneFormValues) => setDraft(values), []);

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={zone === null ? t('builder.newZoneTitle') : t('builder.editZoneTitle')}
      size="lg"
    >
      <div className="flex flex-col gap-4">
        <p className="text-text-muted text-sm">{t('builder.newZoneIntro')}</p>

        {/* Sig'masligi TERISH PAYTIDA aytiladi, "Qo'shish" bosilgandan keyin
            emas: odam o'lchamni qayta o'ylashi kerak bo'lsa, hozir bilsin. */}
        {draft !== null && spot === null ? (
          <p
            role="alert"
            className={cn(
              'border-warn/25 flex items-start gap-2 rounded-[var(--radius-sm)] border',
              'bg-warn-soft text-warn px-3 py-2 text-sm',
            )}
          >
            <AlertTriangle size={16} aria-hidden className="mt-0.5 shrink-0" />
            {t('builder.newZoneNoRoom')}
          </p>
        ) : null}

        <ZoneEditor
          zone={zone}
          floor={floor}
          autoPlace
          submitLabel={zone === null ? t('builder.newZoneSubmit') : t('builder.save')}
          defaultCode={nextCode(zones)}
          onChange={handleChange}
          onCancel={onClose}
          onSave={(values) => {
            if (spot === null) return;
            onSubmit({ ...values, x: spot.x, y: spot.y });
            setDraft(null);
            onClose();
          }}
          key={zone?.id ?? 'new'}
        />
      </div>
    </Dialog>
  );
};
